import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface KeyboardShortcutsConfig {
  onShowHelp?: () => void;
  onQuizAnswer?: (choiceIndex: number) => void;
  onQuizNext?: () => void;
  onQuizExit?: () => void;
  disabled?: boolean;
}

export function useKeyboardShortcuts(config: KeyboardShortcutsConfig = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (config.disabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Help modal: ? key
      if (e.key === '?' && config.onShowHelp) {
        e.preventDefault();
        config.onShowHelp();
        return;
      }

      // Quiz shortcuts (only on quiz page)
      if (location.pathname === '/quiz') {
        // Number keys 1-4 for quiz choices
        if (['1', '2', '3', '4'].includes(e.key) && config.onQuizAnswer) {
          e.preventDefault();
          config.onQuizAnswer(parseInt(e.key) - 1);
          return;
        }

        // Enter for next question
        if (e.key === 'Enter' && config.onQuizNext) {
          e.preventDefault();
          config.onQuizNext();
          return;
        }

        // Escape to exit quiz
        if (e.key === 'Escape' && config.onQuizExit) {
          e.preventDefault();
          config.onQuizExit();
          return;
        }
      }

      // Global navigation shortcuts (Alt + key)
      if (e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'h':
            e.preventDefault();
            navigate('/');
            break;
          case 'd':
            e.preventDefault();
            navigate('/dashboard');
            break;
          case 'q':
            e.preventDefault();
            navigate('/quiz');
            break;
          case 'p':
            e.preventDefault();
            navigate('/portfolio');
            break;
          case 'n':
            e.preventDefault();
            navigate('/diary');
            break;
          case 'm':
            e.preventDefault();
            navigate('/materials');
            break;
          case 'v':
            e.preventDefault();
            navigate('/videos');
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, location, config]);
}
